import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search } from 'lucide-react';
import api from '@/lib/api';
import { MappingReviewRow, SynonymBundleRow } from './MappingReviewRow';

const FILTERS = [
  ['all', 'All', () => true],
  ['used', 'Used', r => r.used],
  ['unused', 'Unused', r => !r.used],
  ['custom', 'Custom', r => r.source === 'custom'],
  ['default', 'Default', r => ['default', 'industry', 'fuzzy'].includes(r.source)],
  ['unmapped', 'Unmapped', r => r.used && r.source === 'fallback'],
  ['review', 'Needs Review', r => r.needs_review],
];

// Unused, default-only rows collapse into one "+N synonyms" line per account.
const isSynonym = r => !r.used && r.source !== 'custom' && !r.flags.length;

function bundle(rows) {
  const items = [];
  const byAcc = {};
  rows.forEach(r => {
    if (!isSynonym(r)) { items.push({ type: 'row', row: r }); return; }
    const k = r.account.code || r.account.name;
    if (!byAcc[k]) { byAcc[k] = { type: 'bundle', account: r.account, rows: [] }; items.push(byAcc[k]); }
    byAcc[k].rows.push(r);
  });
  return items;
}

export function MappingReviewView({ onEdit }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/accounting/category-account-mappings/review').then(r => setData(r.data)).catch(() => setData({ rows: [], groups: [] }));
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fn = FILTERS.find(f => f[0] === filter)[2];
    return (data?.rows || []).filter(fn).filter(r => !q || r.label.toLowerCase().includes(q)
      || (r.account.name || '').toLowerCase().includes(q) || r.synonyms.some(s => s.includes(q)));
  }, [data, search, filter]);

  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="review-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="mapping-review-view">
      <Card><CardContent className="p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search categories, synonyms or accounts..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10" data-testid="review-search-input" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="review-filters">
          {FILTERS.map(([k, l, fn]) => (
            <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)} data-testid={`review-filter-${k}`}>
              {l} <span className="ml-1.5 text-[10px] opacity-70" data-testid={`review-count-${k}`}>{data.rows.filter(fn).length}</span>
            </Button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">Groups are for display only and do not change accounting. Suggestions are review-only — nothing is applied automatically.</p>
      </CardContent></Card>

      {data.groups.map(g => {
        const items = bundle(rows.filter(r => r.group === g));
        if (!items.length) return null;
        return (
          <Card key={g} data-testid={`review-group-${g.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <CardContent className="p-0">
              <div className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30 flex justify-between">
                <span>{g}</span><span>{items.length}</span>
              </div>
              <div className="divide-y divide-border">
                {items.map(it => it.type === 'row'
                  ? <MappingReviewRow key={it.row.key} row={it.row} onEdit={onEdit} />
                  : <SynonymBundleRow key={`b-${it.account.code || it.account.name}`} account={it.account} rows={it.rows} />)}
              </div>
            </CardContent>
          </Card>
        );
      })}
      {!rows.length && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="review-empty">No categories match</div>}
    </div>
  );
}
